/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react"
import { Dialog, DialogContent, DialogActions } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import {
  StyledModalTitleContainer,
  StyledModalTitle,
  FieldRow,
  FieldColumn,
  FieldLabel,
  FieldValue,
  StyledPhoneField,
  StyledCreateButton,
  LoaderContainer,
  PhoneWarning,
} from "./styles"
import { ModalCancelButton } from "../modal_cancel_button/index"
import { Loader } from "../loader"
import { LoadingSpinner } from "../loading_spinner"
import { validatePhoneNumber } from "../../utils/validatePhoneNumber"

export const CreateRecordModal = ({
  open,
  handleClose,
  data,
  isLoading,
  phoneNumber,
  setPhoneNumber,
  handleCreate,
  isCreating,
}) => {
  const hasStoredPhone = !!data?.conflict_phone
  const isPhoneValid = hasStoredPhone || validatePhoneNumber(phoneNumber)
  const canCreate = !!data?.first_name && !!data?.last_name && isPhoneValid

  return (
    <Dialog
      data-testid='create-record-modal'
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          minHeight: '260px',
        },
      }}
    >
      {
        isLoading ? (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        ) : (
          <React.Fragment>
            <DialogContent sx={{ padding: '10px 20px' }}>
              <StyledModalTitleContainer>
                <StyledModalTitle>
                  Create record
                </StyledModalTitle>
                <CloseIcon
                  data-testid="cross-icon"
                  onClick={handleClose}
                  sx={{ cursor: 'pointer', color: 'grey' }}
                />
              </StyledModalTitleContainer>

              <FieldRow>
                <FieldColumn>
                  <FieldLabel>First name</FieldLabel>
                  <FieldValue>{data?.first_name || '-'}</FieldValue>
                </FieldColumn>
                <FieldColumn>
                  <FieldLabel>Last name</FieldLabel>
                  <FieldValue>{data?.last_name || '-'}</FieldValue>
                </FieldColumn>
              </FieldRow>

              <FieldRow>
                <FieldColumn>
                  <FieldLabel>Phone number</FieldLabel>
                  {
                    hasStoredPhone ? (
                      <FieldValue>{data.conflict_phone}</FieldValue>
                    ) : (
                      <React.Fragment>
                        <StyledPhoneField
                          value={phoneNumber}
                          onChange={(value) => setPhoneNumber(value)}
                          defaultCountry="us"
                          forceDialCode
                          borderColor="#c4c4c4"
                          inputProps={{ 'data-testid': 'create-record-phone' }}
                        />
                        {
                          !isPhoneValid && (
                            <PhoneWarning label="Phone number missing, add one to continue" />
                          )
                        }
                      </React.Fragment>
                    )
                  }
                </FieldColumn>
              </FieldRow>
            </DialogContent>

            <DialogActions sx={{ padding: '20px' }}>
              <ModalCancelButton
                data-testid='close-modal'
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </ModalCancelButton>
              <StyledCreateButton
                data-testid='create-record-btn'
                variant="contained"
                onClick={handleCreate}
                disabled={!canCreate || isCreating}
              >
                {isCreating ? <LoadingSpinner isLoading={true} /> : 'Create record'}
              </StyledCreateButton>
            </DialogActions>
          </React.Fragment>
        )
      }
    </Dialog>
  )
}
